import { ScrollToTop } from "@/shared/lib/ScrollToTop";
import { replaceEmptyStringWithUndefined } from "@/shared/lib/object";
import {
  customZodResolver,
  useSubscribeFromValues,
} from "@/shared/lib/react-hook-form";
import { useLoggingEffect } from "@/shared/lib/react-hooks";
import { BaseButton } from "@/shared/ui/BaseButton";
import { BaseBreadCrumb } from "@/shared/ui/base-bread-crumb";
import { BaseMessageCard } from "@/shared/ui/base-message-card";
import { DeliveryProposalRejectionDialog } from "@/widgets/delivery-proposal-rejection-dialog";
import { Box, Container, Stack, Typography, useTheme } from "@mui/material";
import type React from "react";
import { useCallback, useState } from "react";
import { type FieldError, useForm } from "react-hook-form";
import { calculateDeliveryProposalInputValues } from "../../input-values";
import type { AdminDeliveryProposalDetailPageProps } from "../../model";
import {
  type CreateDeliveryProposalFormSchemaType,
  createDeliveryProposalFormSchema,
} from "../../model/validation";
import { ContractTable } from "./contract-table";
import { CompanyForm } from "./forms/company-form";
import { DestinationsForm } from "./forms/destinations-form";
import { MemoForm } from "./forms/memo-form";
import { PaymentForm } from "./forms/payment-form";
import { VehicleForm } from "./forms/vehicle-form";

export const DeliveryProposalDetailInProgress: React.FC<
  AdminDeliveryProposalDetailPageProps
> = ({
  deliveryProposalOfferCargo,
  draftOfferCargo,
  companies,
  contractOfferCargos,
  defaultInputValues,
  vehicles,
  onConfirmClick,
  onInputValuesUpdate,
  onRejectClick,
  deliveryProposalId,
}) => {
  const theme = useTheme();
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);

  const links = [
    { href: "/admin/cargos", label: "荷物一覧" },
    {
      href: draftOfferCargo.data
        ? `/admin/cargos/${draftOfferCargo.data.id}`
        : undefined,
      label: "荷物詳細",
    },
    { label: "配送提案詳細" },
  ];

  const { handleSubmit, getValues, reset, trigger, ...form } =
    useForm<CreateDeliveryProposalFormSchemaType>({
      resolver: async (values, context, options) => {
        const resolved = await customZodResolver(
          createDeliveryProposalFormSchema,
        )(values, context, options);
        let shippingFeeError: FieldError | undefined = undefined;
        if (
          draftOfferCargo.data &&
          values.shippingFee &&
          draftOfferCargo.data.shippingFee > values.shippingFee
        ) {
          shippingFeeError = {
            type: "cant_propose_lower_shipping_fee",
            message: "荷主要望の運賃よりも低い価格を提案することはできません。",
          };
        }
        const errors = {
          ...resolved.errors,
          ...(shippingFeeError ? { shippingFee: shippingFeeError } : {}),
        };
        // remove undefined keys
        for (const key in errors) {
          if ((errors as Record<string, unknown>)[key] === undefined) {
            delete (errors as Record<string, unknown>)[key];
          }
        }
        return {
          errors,
          values: resolved.values,
        };
      },
      mode: "onBlur",
      reValidateMode: "onChange",
    });
  useLoggingEffect(
    () => {
      if (defaultInputValues) {
        reset(defaultInputValues);
        trigger();
        return;
      }
      // deliveryProposalOfferCargoのロードを待って、デフォルト値の設定を行う
      // OfferCargoの構造とUIの構造が異なるため変換している
      const convertedDeliveryProposalOfferCargo =
        deliveryProposalOfferCargo.data
          ? calculateDeliveryProposalInputValues(
              deliveryProposalOfferCargo.data,
            )
          : undefined;
      reset({
        companyId: draftOfferCargo.data?.company?.id,
        ...convertedDeliveryProposalOfferCargo,
      });
      trigger();
    },
    [deliveryProposalOfferCargo.data, draftOfferCargo.data],
    "DeliveryProposalCreateInProgress",
  );
  useSubscribeFromValues((data) => {
    onInputValuesUpdate(replaceEmptyStringWithUndefined(data));
  }, form.watch);

  const handleComfirmClick = useCallback(() => {
    handleSubmit((data) => {
      if (!draftOfferCargo.data) {
        return;
      }
      onConfirmClick(data);
    })();
  }, [draftOfferCargo.data, handleSubmit, onConfirmClick]);

  const hasErrors = Object.keys(form.formState.errors).length > 0;

  return (
    <Stack px={{ xs: 2, sm: 4 }} pb={2}>
      <ScrollToTop />
      <BaseBreadCrumb links={links} sx={{ mb: 3 }} />

      <Box sx={{ mb: { xs: 3, sm: 4 } }}>
        <Typography variant="h5" fontWeight={700}>
          配送提案詳細
        </Typography>
      </Box>

      <Stack
        py={4}
        sx={{
          backgroundColor: theme.palette.grey[50],
          mb: { xs: 3, sm: 4 },
          mx: { xs: -2, sm: 0 },
        }}
      >
        <Container sx={{ width: { sm: 920 }, px: 0 }}>
          <Stack spacing={2}>
            <CompanyForm
              companies={companies.status === "success" ? companies.data : null}
              companyId={form.watch("companyId")}
            />
            <DestinationsForm {...form} draftOfferCargo={draftOfferCargo} />
            <PaymentForm {...form} draftOfferCargo={draftOfferCargo} />
            <VehicleForm
              {...form}
              draftOfferCargo={draftOfferCargo}
              vehicles={vehicles}
              defaultSelectedVehicleId={
                defaultInputValues?.vehicleId ?? undefined
              }
            />
            <MemoForm {...form} draftOfferCargo={draftOfferCargo} />
          </Stack>
        </Container>
      </Stack>

      <DeliveryProposalRejectionDialog
        open={isRejectDialogOpen}
        onClose={() => setIsRejectDialogOpen(false)}
        onSubmit={(params) => {
          setIsRejectDialogOpen(false);
          onRejectClick(params);
        }}
      />

      <Stack spacing={2} p={4} alignItems="center" justifyContent="center">
        {hasErrors && (
          <BaseMessageCard
            status="error"
            message={
              "正しく入力されていない項目があります。再度ご確認ください。"
            }
          />
        )}
        <Stack direction="row" spacing={2}>
          <BaseButton
            variant="outlined"
            color="primary"
            sx={{ width: "240px" }}
            onClick={() => setIsRejectDialogOpen(true)}
          >
            見送る
          </BaseButton>
          <BaseButton
            type="submit"
            variant="contained"
            color="primary"
            sx={{ width: "240px" }}
            onClick={handleComfirmClick}
          >
            確認する
          </BaseButton>
        </Stack>
      </Stack>

      {/* 契約一覧テーブル */}
      <Container sx={{ width: { sm: 920 }, px: 0, mb: 4 }}>
        <ContractTable
          contractOfferCargos={contractOfferCargos}
          deliveryProposalId={deliveryProposalId}
        />
      </Container>
    </Stack>
  );
};
